/**
 * Date handling.
 *
 * The wire carries ISO 8601 strings, and this package hands back a `Date` wherever the contract
 * declares an instant, so no caller writes `new Date(itinerary.created_at)` or remembers which
 * fields are timestamps. Wherever the contract declares none, a string stays a string however
 * much it looks like a timestamp: a track named by its device's clock, a property value an author
 * wrote as a date, a draft a peer is typing.
 *
 * The outbound direction needs no counterpart. `Date.prototype.toJSON` already serializes
 * to ISO 8601, so a `Date` placed in a request body survives `JSON.stringify` correctly
 * without help.
 */

/**
 * DateMask names the instants in a JSON value: the strings a response decodes to a `Date`.
 *
 * `true` marks the value itself. An object marks each field it names by that field's own mask,
 * and leaves every field it does not name as it arrived. A one-element array marks every element
 * of an array by that element's mask. A generated operation states the mask its contract
 * declares, so a field decodes to a `Date` exactly where its generated type says `Date`.
 *
 * @example
 * ```ts
 * // An envelope whose `data` is a list of zones, each carrying two instants.
 * const dates: DateMask = { data: [{ created_at: true, updated_at: true }] };
 * ```
 */
export type DateMask = true | { readonly [field: string]: DateMask } | readonly [DateMask];

/**
 * isoDate matches an ISO 8601 date-time carrying a zone, and captures the fields the calendar
 * check reads: year, month, day, hour, minute, second, and the offset's hours and minutes.
 *
 * Anchored and specific even inside a declared field: a date-only value, a time with no zone
 * and a sentence that contains a timestamp name no instant a caller could rely on, so each stays
 * the string it was.
 */
const isoDate =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/;

/** The shortest string {@link isoDate} can match: `2026-08-25T12:34:56Z`. */
const ISO_DATE_MIN_LENGTH = 20;

/** `T`, the separator {@link isoDate} requires at index 10. */
const ISO_DATE_SEPARATOR = 84;

/** The days in each month of a common year, January first. */
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** isLeapYear reports whether a year of the proleptic Gregorian calendar has a leap day. */
const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/** daysIn answers how many days a month of a year has, January being 1. */
const daysIn = (year: number, month: number): number =>
  month === 2 && isLeapYear(year) ? 29 : (MONTH_DAYS[month - 1] ?? 0);

/**
 * instantOf answers the `Date` a string names, or undefined where it names none a `Date` holds.
 *
 * Two comparisons run before the pattern: anything shorter than {@link ISO_DATE_MIN_LENGTH}
 * cannot match it, and the pattern fixes a literal `T` at index 10. Both are derived from the
 * pattern, so they reject nothing it would accept, and an encoded geometry of several thousand
 * characters costs the same two comparisons as a short name.
 *
 * The calendar is checked after the shape. `new Date` reads `2026-02-30T00:00:00Z` as the second
 * of March, a different instant from the one the value names, and reads a thirteenth month or a
 * sixtieth second as an Invalid Date. Neither is an instant the API wrote, so each stays the
 * string it was rather than failing the response it arrived in.
 */
const instantOf = (value: string): Date | undefined => {
  if (value.length < ISO_DATE_MIN_LENGTH || value.charCodeAt(10) !== ISO_DATE_SEPARATOR) {
    return undefined;
  }

  const fields = isoDate.exec(value);
  if (fields === null) {
    return undefined;
  }

  // An offset of `Z` captures nothing, which reads as zero hours and zero minutes.
  const field = (index: number): number => Number(fields[index] ?? 0);
  const year = field(1);
  const month = field(2);
  const day = field(3);
  const named =
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysIn(year, month) &&
    field(4) <= 23 &&
    field(5) <= 59 &&
    field(6) <= 59 &&
    field(7) <= 23 &&
    field(8) <= 59;
  if (!named) {
    return undefined;
  }

  const instant = new Date(value);

  return Number.isNaN(instant.getTime()) ? undefined : instant;
};

/** isListMask reports whether a mask marks every element of a list. */
const isListMask = (mask: DateMask): mask is readonly [DateMask] => Array.isArray(mask);

/** isList reports whether a value is a list, as one of unknown elements. */
const isList = (value: unknown): value is unknown[] => Array.isArray(value);

/**
 * decodeDates decodes to a `Date` each instant `mask` names in `value`, in place, and answers
 * the value: the object or list it was given, or the `Date` a string at the root decodes to.
 *
 * A string the mask marks becomes a `Date` only where it is a full ISO 8601 timestamp carrying
 * a zone and naming an instant a `Date` holds; every other string stays one, the ones the mask
 * does not reach above all. A value whose shape differs from its mask (a `null` where an object
 * is declared, an object where a list is) is left as it arrived: a body that disagrees with its
 * declaration is the server's to answer for, and no response fails over one field. A field the
 * value does not own is never read, so a mask cannot reach an inherited member.
 *
 * With no mask, nothing is decoded.
 */
export const decodeDates = (value: unknown, mask: DateMask | undefined): unknown => {
  if (mask === undefined) {
    return value;
  }

  if (mask === true) {
    return typeof value === "string" ? (instantOf(value) ?? value) : value;
  }

  if (isListMask(mask)) {
    if (isList(value)) {
      const [element] = mask;
      for (let index = 0; index < value.length; index += 1) {
        value[index] = decodeDates(value[index], element);
      }
    }

    return value;
  }

  if (typeof value === "object" && value !== null && !isList(value)) {
    for (const [field, fieldMask] of Object.entries(mask)) {
      if (Object.hasOwn(value, field)) {
        const current: unknown = Reflect.get(value, field);
        const decoded = decodeDates(current, fieldMask);

        // Only a string becomes something else; an object or a list is decoded where it lies.
        if (decoded !== current) {
          Reflect.set(value, field, decoded);
        }
      }
    }
  }

  return value;
};

/**
 * parseJsonWithDates parses a response body and decodes to a `Date` each instant `mask` names,
 * as {@link decodeDates} does. With no mask, it decodes none.
 *
 * The parse takes no reviver: a reviver is called for every value in the body, coordinates
 * included, and the mask visits only the fields that can hold an instant, so a multi-megabyte
 * itinerary costs about what `JSON.parse` alone does (`bench/dates.bench.ts` measures both).
 *
 * An empty body yields `undefined` rather than throwing. That case is real and routine:
 * every delete of the Map Studio API answers `200` with a `NoData` envelope, and several
 * endpoints answer with no body at all, so a parser that threw on empty would turn the
 * success path of a whole verb into an error.
 *
 * @throws `SyntaxError` for a body that is not JSON.
 */
export const parseJsonWithDates = (text: string, mask?: DateMask): unknown =>
  text.length > 0 ? decodeDates(JSON.parse(text), mask) : undefined;
